import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { signup, login } from "../api/auth";
import { getProfile } from "../api/agents";
import { createBulkCheckout, listLeadTypes } from "../api/subscriptions";
import { useAuthStore } from "../store/authStore";
import { Button, Input, Label, Spinner } from "../components/common/UIKit";
import { Database, Eye, EyeOff } from "lucide-react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useSiteName } from "../hooks/useSiteName";
import { errMsg } from "../lib/utils";
import { getAnonymousId } from "../utils/anonymousId";
import { abandonedCartApi } from "../api/abandonedCarts";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/cf0c5174-481d-49d7-b7d1-7024e4fe2710/images/3ddb46e773450f7290cab9edd9887d1087e0794400b1640dcf790bccc2b2db9f.png";

export default function SignupPage() {
  const [form, setForm] = useState({ email: "", full_name: "", phone_number: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuthStore();
  const siteName = useSiteName();

  // Pull counties & lead types from URL params populated by landing-page calculator.
  // URL shape: ?plan=per-type&county_0_state=CA&county_0_name=Alameda&...&lead_types=fsbo,probate  (or lead_types=all)
  const parsePlanParams = () => {
    const checkoutPlan = searchParams.get("checkout_plan");
    if (checkoutPlan) {
      try {
        return { checkoutPayload: JSON.parse(checkoutPlan) };
      } catch {
        return null;
      }
    }
    const plan = searchParams.get("plan");
    if (!plan) return null;
    const counties = [];
    for (let i = 0; i < 10; i += 1) {
      const state = searchParams.get(`county_${i}_state`);
      const name = searchParams.get(`county_${i}_name`);
      if (!state || !name) break;
      counties.push({ state, county_name: name });
    }
    const raw = searchParams.get("lead_types");
    const is_all_types = plan === "all-types";
    let lead_types = [];
    if (raw && raw !== "all") lead_types = raw.split(",").map((s) => s.trim()).filter(Boolean);
    return { plan, counties, lead_types, is_all_types };
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(form.password)) {
      toast.error("Password must include at least one special character");
      return;
    }
    setLoading(true);
    try {
      await signup(form);
      const tokenRes = await login({ email: form.email, password: form.password });
      useAuthStore.setState({ token: tokenRes.access_token });
      const me = await getProfile();
      setAuth({ token: tokenRes.access_token, user: me });
      toast.success("Account created");

      const planInfo = parsePlanParams();
      if (planInfo?.checkoutPayload || (planInfo && planInfo.counties.length > 0)) {
        setCheckoutLoading(true);
        try {
          if (planInfo.checkoutPayload) {
            const res = await createBulkCheckout(planInfo.checkoutPayload);
            window.location.href = res.session_url;
            return;
          }
          // If landing page sent `lead_types=all` on per-type plan (3+ counties flat),
          // expand "all" into the real lead type names.
          let leadTypes = planInfo.lead_types;
          const raw = searchParams.get("lead_types");
          if (raw === "all" && !planInfo.is_all_types) {
            const allLeadTypes = await listLeadTypes();
            leadTypes = allLeadTypes.map((lt) => lt.name);
          }
          const res = await createBulkCheckout({
            counties: planInfo.counties,
            lead_types: leadTypes,
            is_all_types: planInfo.is_all_types,
            billing: searchParams.get("billing") === "yearly" ? "yearly" : "monthly",
          });
          window.location.href = res.session_url;
          return;
        } catch (checkoutErr) {
          toast.error(errMsg(checkoutErr, "Could not start checkout"));
          navigate("/subscriptions");
          return;
        }
      }
      navigate("/subscriptions");
    } catch (err) {
      toast.error(errMsg(err, "Signup failed"));
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleIdentityBlur = () => {
    if (!form.email && !form.full_name && !form.phone_number) return;
    const planInfo = parsePlanParams();
    if (!planInfo) return;

    let leadTypes = planInfo.lead_types || [];
    if (planInfo.checkoutPayload?.lead_types) {
      leadTypes = planInfo.checkoutPayload.lead_types.map(lt => lt.name || lt);
    }
    const counties = planInfo.counties || planInfo.checkoutPayload?.lead_types?.flatMap?.(lt => lt.counties) || [];

    const data = {
      anonymous_id: getAnonymousId(),
      email: form.email || undefined,
      full_name: form.full_name || undefined,
      phone_number: form.phone_number || undefined,
      lead_types: leadTypes,
      counties: counties,
      plan: planInfo.checkoutPayload?.plan || "Per Lead Type",
      billing: searchParams.get("billing") === "yearly" ? "yearly" : "monthly",
    };

    abandonedCartApi.track(data).catch(() => {});
  };

  if (checkoutLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-white" data-testid="signup-checkout-loading">
        <Spinner />
        <p className="text-sm font-semibold text-zinc-950">Setting up your subscription...</p>
        <p className="text-xs text-zinc-500">Redirecting to Stripe</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="signup-form">
          <h1 className="text-4xl font-black tracking-tight text-zinc-950 mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Create account
          </h1>
          <p className="text-sm text-zinc-500 mb-8">Start accessing qualified leads in minutes.</p>

          <div className="mb-4">
            <Label>Name *</Label>
            <Input data-testid="signup-name-input" value={form.full_name} onChange={set("full_name")} onBlur={handleIdentityBlur} required placeholder="Jane Agent" />
          </div>
          <div className="mb-4">
            <Label>Email *</Label>
            <Input data-testid="signup-email-input" type="email" value={form.email} onChange={set("email")} onBlur={handleIdentityBlur} required placeholder="you@agency.com" />
          </div>
          <div className="mb-4">
            <Label>Phone</Label>
            <div
              data-testid="signup-phone-wrapper"
              className="flex h-10 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus-within:border-zinc-950"
            >
              <PhoneInput
                international
                defaultCountry="US"
                value={form.phone_number || undefined}
                onChange={(val) => setForm((f) => ({ ...f, phone_number: val || "" }))}
                onBlur={handleIdentityBlur}
                placeholder="(555) 123-4567"
                data-testid="signup-phone-input"
                className="flex-1 flex items-center gap-2"
                numberInputProps={{
                  className: "w-full outline-none bg-transparent text-zinc-950 placeholder:text-zinc-400",
                }}
              />
            </div>
          </div>
          <div className="mb-6">
            <Label>Password *</Label>
            <div className="relative">
              <Input
                data-testid="signup-password-input"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={set("password")}
                required
                placeholder="Min 6 chars, 1 special character"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <Button data-testid="signup-submit-button" type="submit" className="w-full" disabled={loading}>
            {loading ? "Creating..." : "Create account"}
          </Button>
          <p className="text-sm text-zinc-500 mt-6 text-center">
            By creating an account, you agree to our{" "}
            <Link to="/privacy-policy" className="font-semibold text-zinc-950 hover:underline" data-testid="signup-privacy-link">
              Privacy Policy
            </Link>
            .
          </p>
          <p className="text-sm text-zinc-500 mt-3 text-center">
            Have an account?{" "}
            <Link to="/login" className="font-semibold text-zinc-950 hover:underline" data-testid="go-to-login-link">
              Sign in
            </Link>
          </p>
        </form>
      </div>

      <div className="hidden lg:flex relative bg-zinc-950 overflow-hidden order-1 lg:order-2">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-bl from-zinc-950/80 via-zinc-900/50 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-2 text-white">
            <Database className="h-5 w-5" strokeWidth={2.5} />
            <span
              onClick={() => navigate('/')}
              className="text-base font-black tracking-tight cursor-pointer"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              data-testid="signup-site-name"
            >
              {siteName}
            </span>
          </div>
          <div className="text-white max-w-md">
            <h2 className="text-5xl font-black tracking-tight leading-none mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Less prospecting. More closing.
            </h2>
            <p className="text-zinc-300 text-sm leading-relaxed">
              Target counties, lock in lead types, and pull fresh listings — all from one control room.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
